/**
 * EnergyControlDemoExperience — Iter 57 dashboard-stijl demo.
 *
 * Layout (mobile-first, user spec):
 *
 *   ┌─────────────────────────────────────────────────────────┐
 *   │ 1. DemoHero          (titel + uitleg + CTA bovenaan)    │
 *   │ 2. ScoreCard         (☀️ score · 💶 kans · ⏰ goedkoopste │
 *   │                       · 🔌 apparaten gezien)            │
 *   │ 3. 6 Insight cards   (eigen zon, tarief, apparaten,     │
 *   │                       piek, salderen, volgende stap)    │
 *   │ 4. CTA (mid)         "Start Energy Control · €9,95/m"   │
 *   │ 5. Appliance breakdown (bespaarpunten)                  │
 *   │ 6. Wat doet Powersure wel/niet                          │
 *   │ 7. CTA (final)                                          │
 *   └─────────────────────────────────────────────────────────┘
 *
 * Hard rules:
 *   - Every module carries `is_demo:true` + visible "Voorbeeld" badge.
 *   - No guaranteed-savings claims; copy uses "indicatief".
 */
import { useEffect, useRef } from 'react';
import {
  Sun, Clock, Plug, AlertTriangle, FileText,
  CheckCircle2, Shield, ArrowRight, Info, Calculator,
} from 'lucide-react';
import EnergyControlCheckoutButton from '@/components/fase1/EnergyControlCheckoutButton';

const DemoBadge = () => (
  <span
    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
    data-testid="demo-badge"
  >
    <Info className="w-3 h-3" />
    Voorbeeld
  </span>
);

function ModuleCard({ id, title, icon: Icon, accent = 'primary', children, onView }) {
  const ref = useRef(null);
  useEffect(() => { if (onView) onView(id); }, [id, onView]);
  const accents = {
    primary: 'bg-primary/10 text-primary',
    emerald: 'bg-emerald-100 text-emerald-700',
    blue:    'bg-sky-100 text-sky-700',
    amber:   'bg-amber-100 text-amber-700',
    rose:    'bg-rose-100 text-rose-700',
  };
  return (
    <section
      ref={ref}
      className="rounded-2xl border border-border bg-white p-4 sm:p-5 shadow-sm"
      data-testid={`demo-module-${id}`}
    >
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${accents[accent] || accents.primary}`}>
            <Icon className="w-4 h-4" />
          </span>
          <h3 className="text-base sm:text-lg font-medium text-primary leading-tight" style={{ fontFamily: 'Outfit' }}>
            {title}
          </h3>
        </div>
        <DemoBadge />
      </div>
      {children}
    </section>
  );
}

// ---------- Score card (boven) ---------- //

function ScoreCard({ demo, modules }) {
  const score = modules.zonnestroom_score;
  const breakdown = modules.appliance_breakdown;
  const tarief = modules.tarief_timing;
  const devices = modules.device_hub_preview;
  const cheapestHour = tarief.cheapest_hour;
  const deviceLabels = devices.devices.slice(0, 4).map((d) => d.label);

  return (
    <section
      className="rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/8 to-background p-4 sm:p-5 shadow-sm mb-5"
      data-testid="demo-scorecard"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-primary">
          Uw demo-overzicht
        </p>
        <DemoBadge />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-lg bg-amber-50 border border-amber-100 p-3">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-semibold text-amber-800">
            <Sun className="w-3.5 h-3.5" /> Zonnestroom Score
          </div>
          <div className="mt-1 text-2xl font-semibold text-amber-900">{score.score}<span className="text-xs font-normal text-amber-700">/100</span></div>
        </div>
        <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-semibold text-emerald-800">
            ✨ Eerste kans
          </div>
          <div className="mt-1 text-base font-semibold text-emerald-900 leading-tight">
            Aanbevolen
          </div>
          <div className="text-[10px] text-emerald-700">na koppeling concreet</div>
        </div>
        <div className="rounded-lg bg-sky-50 border border-sky-100 p-3">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-semibold text-sky-800">
            <Clock className="w-3.5 h-3.5" /> Goedkoopste uur
          </div>
          <div className="mt-1 text-lg font-semibold text-sky-900">
            {String(cheapestHour.hour).padStart(2, '0')}:00
          </div>
          <div className="text-[10px] text-sky-700">€{cheapestHour.price_eur_per_kwh.toFixed(2)}/kWh</div>
        </div>
        <div className="rounded-lg bg-violet-50 border border-violet-100 p-3">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-semibold text-violet-800">
            <Plug className="w-3.5 h-3.5" /> Apparaten
          </div>
          <div className="mt-1 text-[11px] font-medium text-violet-900 leading-tight">
            {deviceLabels.join(' · ')}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- CTA strip ---------- //

const OBJECTION_KILLERS = [
  'Geen jaarcontract',
  'Maandelijks opzegbaar',
  'Eerste inzicht blijft gratis',
  'Eerst meten voordat u producten koopt',
  'Geen installateur nodig om te starten met inzicht',
];

function CtaStrip({ position, quickscanId, onCtaClick, onStarterKitClick, onInstallationClick }) {
  return (
    <section
      className="rounded-2xl border-2 border-primary/30 bg-primary text-primary-foreground p-4 sm:p-5"
      data-testid={`demo-cta-strip-${position}`}
    >
      <h3 className="text-lg sm:text-xl font-semibold leading-tight" style={{ fontFamily: 'Outfit' }}>
        Activeer Powersure Energy Control
      </h3>
      <p className="text-[13px] opacity-95 mt-1.5 leading-snug">
        Krijg uw volledige plan, maandelijkse optimalisatie, startpakketadvies en duidelijke vervolgstappen.
      </p>
      <p className="text-[12px] opacity-90 mt-2">
        <strong className="font-semibold">€ 9,95</strong> per maand voor de eerste 3 maanden, daarna <strong className="font-semibold">€ 14,95</strong> per maand · maandelijks opzegbaar
      </p>

      <div
        className="mt-3"
        onClickCapture={() => onCtaClick && onCtaClick(`Start Powersure Energy Control (${position})`, 'ec_intro_checkout')}
      >
        <EnergyControlCheckoutButton
          enabled
          email={null}
          intakeId={quickscanId}
          refCode={null}
          label="Start Powersure Energy Control"
          className="w-full bg-white text-primary hover:bg-white/90 border-2 border-white"
        />
      </div>

      {/* Secundaire en tertiaire CTA's — alleen op de finale CTA-strip
          tonen om visuele ruis te voorkomen op de eerste mid-strip. */}
      {position === 'bottom' && (
        <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => { onStarterKitClick && onStarterKitClick(); }}
            className="rounded-md px-4 py-2.5 text-[13px] font-semibold border-2 border-white/70 text-white hover:bg-white/10 transition-colors"
            data-testid={`demo-cta-starterkit-${position}`}
          >
            Bekijk eerst mijn startpakket
          </button>
          <button
            type="button"
            onClick={() => { onInstallationClick && onInstallationClick(); }}
            className="rounded-md px-4 py-2.5 text-[13px] font-semibold border-2 border-white/70 text-white hover:bg-white/10 transition-colors"
            data-testid={`demo-cta-installation-${position}`}
          >
            Vraag installatieadvies aan
          </button>
        </div>
      )}

      {/* Bezwaarverlagers — alleen op finale strip om visuele balans
          te houden in mid-strip. */}
      {position === 'bottom' && (
        <ul
          className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 list-none"
          data-testid="demo-objection-killers"
        >
          {OBJECTION_KILLERS.map((line) => (
            <li
              key={line}
              className="text-[11px] opacity-90 flex items-start gap-1.5 leading-snug"
            >
              <span className="mt-1.5 w-1 h-1 bg-white/80 rounded-full shrink-0" />
              {line}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------- 6 insight cards ---------- //

// P0 mobile-clarity (Feb 2026) — terug van 6 naar 3 "top kansen" zodat de
// klant niet eerst door 6 cards hoeft te scrollen om de primaire CTA te
// zien. De resterende 3 worden via een uitklap-paneel onderaan getoond.
const INSIGHT_CARDS = [
  {
    id: 'eigen-zonnestroom',
    icon: Sun,
    iconBg: 'bg-amber-100 text-amber-700',
    title: 'Eigen zonnestroom slimmer gebruiken',
    line: 'Gebruik meer van uw eigen stroom voordat u teruglevert.',
    color: 'border-emerald-200 bg-emerald-50/40',
  },
  {
    id: 'apparaten',
    icon: Plug,
    iconBg: 'bg-violet-100 text-violet-700',
    title: 'Apparaten koppelen',
    line: 'P1, omvormer, laadpaal, warmtepomp/airco en slimme stekkers in één overzicht.',
    color: 'border-violet-200 bg-violet-50/40',
  },
  {
    id: 'volgende-stap',
    icon: CheckCircle2,
    iconBg: 'bg-emerald-100 text-emerald-700',
    title: 'Beste volgende stap zien',
    line: 'Powersure toont elke maand wat het meest logisch is.',
    color: 'border-emerald-200 bg-emerald-50/40',
  },
];

// Extra insights — alleen zichtbaar achter "Toon meer kansen" accordion
// zodat de mobiele bovenkant rustig blijft.
const EXTRA_INSIGHT_CARDS = [
  {
    id: 'tarief-timing',
    icon: Clock,
    iconBg: 'bg-sky-100 text-sky-700',
    title: 'Tarief & Timing',
    line: 'Plan apparaten op goedkope of zonnige momenten.',
    color: 'border-sky-200 bg-sky-50/40',
  },
  {
    id: 'piekmomenten',
    icon: AlertTriangle,
    iconBg: 'bg-amber-100 text-amber-700',
    title: 'Piekmomenten',
    line: 'Zie wanneer uw woning veel tegelijk verbruikt.',
    color: 'border-amber-200 bg-amber-50/40',
  },
  {
    id: 'salderen-2027',
    icon: FileText,
    iconBg: 'bg-amber-100 text-amber-700',
    title: 'Salderen 2027',
    line: 'Bereid u rustig voor op minder salderen.',
    color: 'border-amber-200 bg-amber-50/40',
  },
];

function InsightCards({ onView }) {
  useEffect(() => { if (onView) onView('insight_cards'); }, [onView]);
  return (
    <section data-testid="demo-insight-cards" className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {INSIGHT_CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.id}
              className={`rounded-2xl border-2 ${c.color} p-3.5 flex items-start gap-3`}
              data-testid={`demo-insight-${c.id}`}
            >
              <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${c.iconBg} shrink-0`}>
                <Icon className="w-4 h-4" />
              </span>
              <div className="min-w-0">
                <div className="font-medium text-sm" style={{ fontFamily: 'Outfit' }}>{c.title}</div>
                <p className="text-[12px] text-foreground/75 mt-0.5 leading-snug">{c.line}</p>
              </div>
            </div>
          );
        })}
      </div>

      <details
        className="group rounded-xl border border-border bg-white px-4 py-3"
        data-testid="demo-insight-more"
      >
        <summary className="flex items-center justify-between cursor-pointer text-sm font-medium text-primary list-none">
          <span>Toon meer kansen</span>
          <span className="text-xs text-muted-foreground group-open:rotate-180 transition-transform">▾</span>
        </summary>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
          {EXTRA_INSIGHT_CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.id}
                className={`rounded-2xl border-2 ${c.color} p-3.5 flex items-start gap-3`}
                data-testid={`demo-insight-${c.id}`}
              >
                <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${c.iconBg} shrink-0`}>
                  <Icon className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <div className="font-medium text-sm" style={{ fontFamily: 'Outfit' }}>{c.title}</div>
                  <p className="text-[12px] text-foreground/75 mt-0.5 leading-snug">{c.line}</p>
                </div>
              </div>
            );
          })}
        </div>
      </details>
    </section>
  );
}

// ---------- Hero ---------- //

function DemoHero({ quickscanId, onCtaClick, onScrollDown }) {
  return (
    <section
      className="rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/8 to-background p-4 sm:p-5 mb-5"
      data-testid="demo-hero"
    >
      <h2
        className="text-2xl sm:text-3xl font-semibold text-primary leading-tight"
        style={{ fontFamily: 'Outfit' }}
        data-testid="demo-hero-h1"
      >
        Uw Energy Control demo
      </h2>
      <p className="text-sm text-foreground/85 mt-1.5" data-testid="demo-intro-block">
        Hier ziet u live wat Powersure voor uw woning vindt. Start Energy Control om elke maand een vers rapport en concrete vervolgstap te ontvangen.
      </p>
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <div
          className="flex-1"
          onClickCapture={() => onCtaClick && onCtaClick('Start Energy Control · € 9,95 eerste 3 mnd · daarna € 14,95 (hero)', 'ec_intro_checkout')}
        >
          <EnergyControlCheckoutButton
            enabled
            email={null}
            intakeId={quickscanId}
            refCode={null}
            label="Start Energy Control · € 9,95 eerste 3 mnd · daarna € 14,95"
            className="w-full"
          />
        </div>
        <button
          type="button"
          onClick={onScrollDown}
          className="px-4 py-2.5 rounded-md border-2 border-primary/30 bg-white text-primary text-sm font-semibold hover:bg-primary/5"
          data-testid="demo-hero-secondary-cta"
        >
          Bekijk eerst de bespaarpunten
        </button>
      </div>
    </section>
  );
}

// ---------- Appliance breakdown ---------- //

function ApplianceBreakdown({ breakdown, onView }) {
  // Neutrale status-labels rouleren door de rijen — geen ongefundeerde euroclaims.
  const STATUS_LABELS = [
    { label: 'Aanbevolen', cls: 'bg-emerald-100 text-emerald-800 border border-emerald-200' },
    { label: 'Eerst meten', cls: 'bg-sky-100 text-sky-800 border border-sky-200' },
    { label: 'Later plannen', cls: 'bg-amber-100 text-amber-800 border border-amber-200' },
    { label: 'Slimme kans', cls: 'bg-emerald-100 text-emerald-800 border border-emerald-200' },
    { label: 'Koppeling aanbevolen', cls: 'bg-sky-100 text-sky-800 border border-sky-200' },
  ];
  return (
    <ModuleCard id="appliance_breakdown" title={breakdown.headline} icon={Calculator} accent="emerald" onView={onView}>
      <p className="text-sm text-foreground/80 mb-3">{breakdown.intro}</p>
      <ul className="space-y-2" data-testid="demo-appliance-breakdown">
        {breakdown.rows.map((r, i) => {
          const status = STATUS_LABELS[i % STATUS_LABELS.length];
          return (
            <li
              key={i}
              className="rounded-lg border border-border bg-white p-3 text-sm flex items-start gap-3"
              data-testid={`demo-breakdown-row-${r.icon}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium">{r.label}</div>
                <p className="text-xs text-foreground/75 mt-0.5 leading-snug">{r.how}</p>
              </div>
              <div className="text-right shrink-0">
                <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${status.cls}`}>
                  {status.label}
                </span>
                <div className="text-[10px] text-muted-foreground mt-1">na koppeling concreet</div>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="demo-breakdown-total">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-emerald-900">Totaal aantal kansen</span>
          <span className="text-base font-semibold text-emerald-900">
            {breakdown.rows.length} stappen
          </span>
        </div>
        <p className="text-[11px] text-emerald-800/80 mt-1.5 leading-snug">
          Concrete bedragen volgen na koppeling van uw apparaten (P1-meter of slimme meter aanbevolen).
        </p>
      </div>
      <p className="text-xs text-muted-foreground mt-2 italic">{breakdown.disclaimer}</p>
    </ModuleCard>
  );
}

// ---------- Wat doet Powersure ---------- //

function WhatPowersureDoes({ wpd, onView }) {
  return (
    <ModuleCard id="what_powersure_does" title="Wat doet Powersure voor u?" icon={Shield} accent="blue" onView={onView}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
          <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold mb-2">Wel</div>
          <ul className="space-y-1.5 text-[13px]">
            {wpd.does.map((d, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3">
          <div className="text-[11px] uppercase tracking-wide text-rose-700 font-semibold mb-2">Niet</div>
          <ul className="space-y-1.5 text-[13px]">
            {wpd.does_not.map((d, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-rose-500 mt-0.5 shrink-0">✕</span>
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </ModuleCard>
  );
}

// ---------- Main ---------- //

export default function EnergyControlDemoExperience({
  demo,
  quickscanId,
  onModuleView,
  onCtaClick,
  onStartDeepDive,
}) {
  const breakdownAnchor = useRef(null);
  if (!demo) return null;
  const { modules } = demo;
  const breakdown = modules.appliance_breakdown;
  const wpd = modules.what_powersure_does;

  const scrollToBreakdown = () => {
    if (breakdownAnchor.current) {
      breakdownAnchor.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-5" data-testid="demo-experience-root">
      <DemoHero
        quickscanId={quickscanId}
        onCtaClick={onCtaClick}
        onScrollDown={scrollToBreakdown}
      />

      <ScoreCard demo={demo} modules={modules} />

      <InsightCards onView={onModuleView} />

      <div ref={breakdownAnchor} />

      {/* P0 mobile-clarity (Feb 2026) — details/uitsplitsing in accordions
          zodat de bovenkant rustig blijft en de primaire CTA snel in beeld
          komt. Klant kan deze blokken op verzoek uitvouwen. */}
      <details
        className="group rounded-2xl border border-border bg-white px-4 sm:px-5 py-3.5"
        data-testid="demo-accordion-breakdown"
      >
        <summary className="flex items-center justify-between cursor-pointer list-none">
          <span className="text-base font-medium text-primary" style={{ fontFamily: 'Outfit' }}>
            Toon alle bespaarkansen
          </span>
          <span className="text-xs text-muted-foreground group-open:rotate-180 transition-transform">▾</span>
        </summary>
        <div className="mt-4">
          <ApplianceBreakdown breakdown={breakdown} onView={onModuleView} />
        </div>
      </details>

      <details
        className="group rounded-2xl border border-border bg-white px-4 sm:px-5 py-3.5"
        data-testid="demo-accordion-what-powersure"
      >
        <summary className="flex items-center justify-between cursor-pointer list-none">
          <span className="text-base font-medium text-primary" style={{ fontFamily: 'Outfit' }}>
            Wat doet Powersure wel en niet
          </span>
          <span className="text-xs text-muted-foreground group-open:rotate-180 transition-transform">▾</span>
        </summary>
        <div className="mt-4">
          <WhatPowersureDoes wpd={wpd} onView={onModuleView} />
        </div>
      </details>

      {/* Final CTA + DeepDive entry */}
      <section
        className="rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-background p-5"
        data-testid="demo-cta-block"
      >
        <h3
          className="text-xl font-semibold text-primary leading-tight"
          style={{ fontFamily: 'Outfit' }}
        >
          Klaar om Energy Control te starten?
        </h3>
        <p className="text-sm text-foreground/80 mt-1.5">
          Direct beginnen voor <strong>€ 9,95 per maand voor de eerste 3 maanden, daarna € 14,95 per maand</strong> · maandelijks opzegbaar.
          Of beantwoord eerst nog 3 extra vragen om uw plan persoonlijker te maken.
        </p>
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => { if (onCtaClick) onCtaClick('Beantwoord 3 extra vragen', 'deepdive'); if (onStartDeepDive) onStartDeepDive(); }}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 border-primary text-primary font-medium hover:bg-primary/5 transition-colors"
            data-testid="demo-cta-deepdive"
          >
            Beantwoord 3 extra vragen
            <ArrowRight className="w-4 h-4" />
          </button>
          <div
            onClickCapture={() => onCtaClick && onCtaClick('Start Energy Control · € 9,95 eerste 3 mnd · daarna € 14,95 (final)', 'ec_intro_checkout')}
            className="flex-1"
            data-testid="demo-cta-checkout-wrap"
          >
            <EnergyControlCheckoutButton
              enabled
              email={null}
              intakeId={quickscanId}
              refCode={null}
              label="Start Energy Control · € 9,95 eerste 3 mnd · daarna € 14,95"
              className="w-full"
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          <strong>€ 9,95 per maand</strong> voor de eerste 3 maanden, daarna <strong>€ 14,95 per maand</strong> · maandelijks opzegbaar · betaling met creditcard of iDEAL · geen leverancierswissel · geen automatische sturing zonder ondersteunde hardware.
        </p>
      </section>
    </div>
  );
}
